// Backorder (continue selling when out of stock) + split-shipment helpers.
// A product opts in with product.continue_selling_oos:
//   true  -> keep selling past stock; over-stock lines get the out-of-stock dispatch text and ship separately
//   false -> stop at stock
//   unset -> legacy behaviour (nothing here applies)
import { useEffect, useState } from "react";
import axios from "axios";

export const DEFAULT_DISPATCH = { inStock: "Dispatch within 1 day", outOfStock: "Dispatch within 10 days" };
let _cache = null;

export const dispatchFromSettings = (settings) => {
  const s = settings?.shipping || {};
  return {
    inStock: (s.dispatch_in_stock_text || "").trim() || DEFAULT_DISPATCH.inStock,
    outOfStock: (s.dispatch_out_of_stock_text || "").trim() || DEFAULT_DISPATCH.outOfStock,
  };
};

export function useDispatchTexts() {
  const [texts, setTexts] = useState(_cache || DEFAULT_DISPATCH);
  useEffect(() => {
    if (_cache) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/checkout-settings`)
      .then(r => { _cache = dispatchFromSettings(r.data); setTexts(_cache); })
      .catch(() => {});
  }, []);
  return texts;
}

export const stockModeOf = (product) =>
  product?.continue_selling_oos === true ? "backorder"
    : product?.continue_selling_oos === false ? "strict" : null;

// Variant-aware version: combo.continue_selling_oos overrides the product-level setting.
// true  → backorder, false → strict, undefined/null → fall back to product level.
export const variantStockMode = (product, combo) => {
  if (typeof combo?.continue_selling_oos === "boolean") {
    return combo.continue_selling_oos ? "backorder" : "strict";
  }
  return stockModeOf(product);
};

export const comboKey = (c) => `${c?.color || ""}|${c?.size || ""}`;

// A cart line is out of stock when the effective mode is backorder and qty exceeds stock.
// line.stockMode (variant-level) takes precedence over item.stockMode (product-level).
export const lineIsBackorder = (item, line) => {
  const mode = line?.stockMode || item?.stockMode;
  return !!mode && mode === "backorder" && typeof line?.stock === "number" && Number(line.qty) > line.stock;
};

// Each variant's own weight wins; the product-level weight is the fallback.
export const lineWeightG = (item, line) =>
  Number(line?.weight) > 0 ? Number(line.weight) : Number(item?.weight || 0);

// In-stock lines ship together; out-of-stock lines ship separately. Carts with no opted-in product stay one shipment.
export function buildShipments(cart) {
  const inStock = [];
  const back = [];
  (cart || []).forEach(item => (item.variantLines || []).forEach(line => {
    const row = { item, line, weightG: lineWeightG(item, line) * Number(line.qty || 0) };
    (lineIsBackorder(item, line) ? back : inStock).push(row);
  }));
  const make = (id, rows) => ({ id, rows, weightG: rows.reduce((s, r) => s + r.weightG, 0) });
  const out = [];
  if (inStock.length) out.push(make("in_stock", inStock));
  if (back.length) out.push(make("backorder", back));
  return out;
}
